/**
 * Church detail (F1): full editable info (incl. timezone),
 * re-anchoring in the tree, relations between churches and enriched VENUES
 * (images, address + coordinates with its own preview, capacity,
 * purposes and opening hours). Each action is gated with `effective_grant` (the
 * same source that decides sync) and the server re-validates with require_grant.
 */
import { msg } from "@lingui/core/macro";
import { createMemo, createSignal, For, Show } from "solid-js";
import {
  getChurchesQuery,
  getChurchRelationsQuery,
  getVenueImagesQuery,
  getVenuesQuery,
  isInSubtree,
  parseSocialLinks,
  patchRowVerified,
  queryKeys,
  serializeSocialLinks,
} from "@agape/shared";
import {
  church_relation,
  type ChurchRecord,
  type ChurchRelationRecord,
  church as churchTable,
  eq,
  venue_image,
  type VenueImageRecord,
  type VenueRecord,
  venue as venueTable,
} from "@agape/powersync";
import { Church as ChurchIcon, ImagePlus, Link2, MapPin, Pencil, Plus, Trash2 } from "@fleet/icons";

import { useI18n } from "@/providers/i18n-provider";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@fleet/ui/card";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { Select } from "@/components/ui/select";
import { Switch } from "@fleet/ui/switch";
import { showToast } from "@/components/ui/toast";
import { resolveStorageUrl } from "@/lib/api";
import { Field, FormDialog, TextArea, UploadField } from "./form";
import { newId, nowIso, useDomainQuery, useGrants, useWriter } from "../use-domain";

const TIMEZONES = [
  "Europe/Madrid",
  "Europe/London",
  "Europe/Berlin",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Mexico_City",
  "America/Bogota",
  "America/Lima",
  "America/Santiago",
  "America/Argentina/Buenos_Aires",
  "America/Sao_Paulo",
  "UTC",
];

const RELATION_KINDS = ["partner", "sister", "mission", "network"] as const;

/** Purpose tags stored as JSON (tolerant to corrupt data). */
function parsePurposes(raw: string | null | undefined): string[] {
  if (!raw) {
    return [];
  }
  try {
    const value: unknown = JSON.parse(raw);
    return Array.isArray(value) ? value.filter((v): v is string => typeof v === "string") : [];
  } catch {
    return [];
  }
}

/** Static coordinate preview (no tile provider: manual integration). */
function GeoPreview(props: { lat: number; lng: number }) {
  const x = () => ((props.lng + 180) / 360) * 100;
  const y = () => ((90 - props.lat) / 180) * 100;
  return (
    <div
      class="relative h-28 w-full overflow-hidden rounded-md border border-border bg-muted/40"
      data-testid="venue-geo-preview"
    >
      <svg class="absolute inset-0 h-full w-full opacity-30" aria-hidden="true">
        <For each={[10, 20, 30, 40, 50, 60, 70, 80, 90]}>
          {(p) => (
            <>
              <line
                x1={`${p}%`}
                y1="0"
                x2={`${p}%`}
                y2="100%"
                stroke="currentColor"
                stroke-width="0.5"
              />
              <line
                x1="0"
                y1={`${p}%`}
                x2="100%"
                y2={`${p}%`}
                stroke="currentColor"
                stroke-width="0.5"
              />
            </>
          )}
        </For>
      </svg>
      <span
        class="absolute flex size-4 -translate-x-1/2 -translate-y-full items-center justify-center text-primary"
        style={{ left: `${x()}%`, top: `${y()}%` }}
      >
        <MapPin size={16} />
      </span>
      <span class="absolute bottom-1 right-2 font-mono text-[10px] text-muted-foreground">
        {props.lat.toFixed(5)}, {props.lng.toFixed(5)}
      </span>
    </div>
  );
}

export function ChurchDetailPage(props: { churchId: string }) {
  const { t } = useI18n();
  const write = useWriter();
  const grants = useGrants();

  const churches = useDomainQuery<ChurchRecord>(
    (uid) => queryKeys.domain.churches(uid),
    (db) => getChurchesQuery(db),
  );
  const relations = useDomainQuery<ChurchRelationRecord>(
    (uid) => queryKeys.domain.churchRelations(uid),
    (db) => getChurchRelationsQuery(db),
  );
  const venues = useDomainQuery<VenueRecord>(
    (uid) => queryKeys.domain.venues(uid),
    (db) => getVenuesQuery(db),
  );
  const images = useDomainQuery<VenueImageRecord>(
    (uid) => queryKeys.domain.venueImages(uid),
    (db) => getVenueImagesQuery(db),
  );

  const current = createMemo(() => (churches.data ?? []).find((c) => c.id === props.churchId));
  const myVenues = createMemo(() =>
    (venues.data ?? []).filter((v) => v.church_id === props.churchId),
  );
  const myRelations = createMemo(() =>
    (relations.data ?? []).filter((r) => r.church_id === props.churchId),
  );
  const canWrite = () => grants.can([props.churchId], "church", "write");
  const canManage = () => grants.can([props.churchId], "church", "manage");

  // ── Edit information ─────────────────────────────────────────────────────
  const [editOpen, setEditOpen] = createSignal(false);
  const [form, setForm] = createSignal<Record<string, string>>({});
  const [isPublic, setIsPublic] = createSignal(false);
  const field = (k: string) => form()[k] ?? "";
  const setField = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const socialOf = (raw: string | null | undefined, kind: string) =>
    parseSocialLinks(raw).find((l) => l.kind === kind)?.url ?? "";

  const openEdit = () => {
    const c = current();
    if (!c) {
      return;
    }
    setForm({
      name: c.name ?? "",
      description: c.description ?? "",
      history: c.history ?? "",
      email: c.email ?? "",
      phone: c.phone ?? "",
      website: c.website ?? "",
      address: c.address ?? "",
      city: c.city ?? "",
      country: c.country ?? "",
      social_whatsapp: socialOf(c.socials, "whatsapp"),
      social_facebook: socialOf(c.socials, "facebook"),
      social_youtube: socialOf(c.socials, "youtube"),
      social_instagram: socialOf(c.socials, "instagram"),
      timezone: c.timezone ?? "Europe/Madrid",
    });
    setIsPublic(Boolean(c.is_public));
    setEditOpen(true);
  };

  const saveEdit = async () => {
    const f = form();
    await patchRowVerified(write(), churchTable, churchTable.id, props.churchId, {
      name: f.name,
      description: f.description || null,
      history: f.history || null,
      email: f.email || null,
      phone: f.phone || null,
      website: f.website || null,
      address: f.address || null,
      city: f.city || null,
      country: f.country || null,
      socials: serializeSocialLinks({
        whatsapp: f.social_whatsapp,
        facebook: f.social_facebook,
        youtube: f.social_youtube,
        instagram: f.social_instagram,
      }),
      timezone: f.timezone,
      is_public: isPublic() ? 1 : 0,
    });
    setEditOpen(false);
    showToast(t(msg`Church updated`), "success");
  };

  // ── Re-anchoring (reparent) ──────────────────────────────────────────────
  const [reparentOpen, setReparentOpen] = createSignal(false);
  const [newParent, setNewParent] = createSignal("");
  const reparentOptions = createMemo(() => {
    const self = current();
    return [
      { value: "", label: t(msg`None — make it a root church`) },
      ...(churches.data ?? [])
        // Neither itself nor its own subtree (anti-cycle — the server re-validates).
        .filter(
          (c) =>
            c.id !== props.churchId && !isInSubtree(self?.path ?? props.churchId, c.path ?? c.id),
        )
        .map((c) => ({ value: c.id, label: c.name ?? c.id })),
    ];
  });
  const reparent = async () => {
    await patchRowVerified(write(), churchTable, churchTable.id, props.churchId, {
      parent_id: newParent() || null,
    });
    setReparentOpen(false);
    showToast(t(msg`Church moved in the tree`), "success");
  };

  // ── Relations between churches ───────────────────────────────────────────
  const [relationOpen, setRelationOpen] = createSignal(false);
  const [relationTarget, setRelationTarget] = createSignal("");
  const [relationKind, setRelationKind] = createSignal<string>("partner");
  const relationTargets = createMemo(() =>
    (churches.data ?? [])
      .filter((c) => c.id !== props.churchId)
      .map((c) => ({ value: c.id, label: c.name ?? c.id })),
  );
  const addRelation = async () => {
    if (!relationTarget()) {
      return;
    }
    await write().insert(church_relation).values({
      id: newId(),
      church_id: props.churchId,
      related_church_id: relationTarget(),
      kind: relationKind(),
      created_at: nowIso(),
      updated_at: nowIso(),
    });
    setRelationOpen(false);
    showToast(t(msg`Relation added`), "success");
  };
  const removeRelation = async (id: string) => {
    await write().delete(church_relation).where(eq(church_relation.id, id));
  };
  const churchName = (id: string | null) =>
    (churches.data ?? []).find((c) => c.id === id)?.name ?? id ?? "";

  // ── Venues ───────────────────────────────────────────────────────────────
  const [venueOpen, setVenueOpen] = createSignal(false);
  const [venueId, setVenueId] = createSignal<string | null>(null);
  const [venueForm, setVenueForm] = createSignal<Record<string, string>>({});
  const vField = (k: string) => venueForm()[k] ?? "";
  const setVField = (k: string, v: string) => setVenueForm((f) => ({ ...f, [k]: v }));

  const openVenue = (v?: VenueRecord) => {
    setVenueId(v?.id ?? null);
    setVenueForm({
      name: v?.name ?? "",
      description: v?.description ?? "",
      address: v?.address ?? "",
      capacity: v?.capacity == null ? "" : String(v.capacity),
      lat: v?.lat == null ? "" : String(v.lat),
      lng: v?.lng == null ? "" : String(v.lng),
      purposes: parsePurposes(v?.purposes).join(", "),
      opening_hours: v?.opening_hours ?? "",
    });
    setVenueOpen(true);
  };

  const saveVenue = async () => {
    const f = venueForm();
    const purposes = (f.purposes ?? "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    const values = {
      name: f.name,
      description: f.description || null,
      address: f.address || null,
      capacity: f.capacity ? Number(f.capacity) : null,
      lat: f.lat ? Number(f.lat) : null,
      lng: f.lng ? Number(f.lng) : null,
      purposes: purposes.length > 0 ? JSON.stringify(purposes) : null,
      opening_hours: f.opening_hours || null,
      updated_at: nowIso(),
    };
    const id = venueId();
    if (id) {
      await patchRowVerified(write(), venueTable, venueTable.id, id, values);
    } else {
      await write()
        .insert(venueTable)
        .values({ id: newId(), church_id: props.churchId, created_at: nowIso(), ...values });
    }
    setVenueOpen(false);
    showToast(t(msg`Venue saved`), "success");
  };

  const trashVenue = async (id: string) => {
    try {
      await patchRowVerified(write(), venueTable, venueTable.id, id, { deleted_at: nowIso() });
      showToast(t(msg`Venue moved to trash`), "success");
    } catch {
      showToast(t(msg`Couldn't save the change — try again`), "error");
    }
  };

  // ── Venue images ─────────────────────────────────────────────────────────
  const [imageVenue, setImageVenue] = createSignal<string | null>(null);
  const [imageUrl, setImageUrl] = createSignal("");
  const [imageCaption, setImageCaption] = createSignal("");
  const venueImages = (vid: string) => (images.data ?? []).filter((i) => i.venue_id === vid);
  const addImage = async () => {
    const vid = imageVenue();
    if (!vid || !imageUrl().trim()) {
      return;
    }
    await write()
      .insert(venue_image)
      .values({
        id: newId(),
        church_id: props.churchId,
        venue_id: vid,
        url: imageUrl().trim(),
        caption: imageCaption().trim() || null,
        sort_order: venueImages(vid).length,
        created_at: nowIso(),
        updated_at: nowIso(),
      });
    setImageVenue(null);
    setImageUrl("");
    setImageCaption("");
    showToast(t(msg`Image added`), "success");
  };
  const removeImage = async (id: string) => {
    await write().delete(venue_image).where(eq(venue_image.id, id));
  };

  return (
    <div class="space-y-6">
      <PageSubHeader title={current()?.name ?? t(msg`Church`)} backTo="/churches" />
      <Show
        when={current()}
        fallback={
          <p class="text-muted-foreground">{t(msg`This church is not synced to your account.`)}</p>
        }
      >
        {(c) => (
          <>
            {/* ── Información ───────────────────────────────────────────── */}
            <Card data-testid="church-info">
              <CardContent class="space-y-3 py-4">
                <div class="flex items-start justify-between gap-3">
                  <div class="min-w-0 space-y-1">
                    <p class="font-semibold">{c().name}</p>
                    <Show when={c().description}>
                      <p class="text-sm text-muted-foreground">{c().description}</p>
                    </Show>
                    <Show when={c().history}>
                      <p class="text-sm text-muted-foreground">{c().history}</p>
                    </Show>
                    <p class="text-xs text-muted-foreground">
                      {[c().address, c().city, c().country].filter(Boolean).join(" · ")}
                    </p>
                    <p class="text-xs text-muted-foreground">
                      {[c().email, c().phone, c().website].filter(Boolean).join(" · ")}
                    </p>
                    <p class="text-xs text-muted-foreground" data-testid="church-timezone">
                      {t(msg`Timezone`)}: {c().timezone ?? "Europe/Madrid"} ·{" "}
                      {c().is_public ? t(msg`Public`) : t(msg`Private`)}
                    </p>
                    {/* Vínculo público (F3): página read-only sin cuenta. */}
                    <Show when={c().is_public}>
                      <p class="text-xs">
                        <a
                          href={`/c/${c().id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          class="text-primary hover:underline"
                          data-testid="church-public-link"
                        >
                          {t(msg`Open public page`)}
                        </a>
                      </p>
                    </Show>
                    <Show when={parseSocialLinks(c().socials).length > 0}>
                      <p class="flex flex-wrap gap-2 text-xs" data-testid="church-socials">
                        <For each={parseSocialLinks(c().socials)}>
                          {(link) => (
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              class="capitalize text-primary hover:underline"
                            >
                              {link.kind}
                            </a>
                          )}
                        </For>
                      </p>
                    </Show>
                  </div>
                  <div class="flex shrink-0 gap-2">
                    <Show when={canWrite()}>
                      <Button
                        size="sm"
                        variant="secondary"
                        data-testid="church-edit"
                        onClick={openEdit}
                      >
                        <Pencil size={14} /> {t(msg`Edit`)}
                      </Button>
                    </Show>
                    <Show when={canManage()}>
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid="church-reparent"
                        onClick={() => {
                          setNewParent(c().parent_id ?? "");
                          setReparentOpen(true);
                        }}
                      >
                        {t(msg`Move in tree`)}
                      </Button>
                    </Show>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* ── Relaciones ────────────────────────────────────────────── */}
            <section class="space-y-2">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(msg`Related churches`)}
                </h3>
                <Show when={canWrite()}>
                  <Button
                    size="sm"
                    variant="ghost"
                    data-testid="relation-add"
                    onClick={() => setRelationOpen(true)}
                  >
                    <Link2 size={14} /> {t(msg`Add relation`)}
                  </Button>
                </Show>
              </div>
              <Show
                when={myRelations().length > 0}
                fallback={<p class="text-sm text-muted-foreground">{t(msg`No relations yet.`)}</p>}
              >
                <For each={myRelations()}>
                  {(r) => (
                    <Card data-testid="relation-item">
                      <CardContent class="flex items-center justify-between gap-3 py-2.5">
                        <p class="min-w-0 truncate text-sm">
                          <span class="font-medium">{churchName(r.related_church_id)}</span>{" "}
                          <span class="text-muted-foreground">({r.kind})</span>
                        </p>
                        <Show when={canWrite()}>
                          <Button
                            size="icon"
                            variant="ghost"
                            aria-label={t(msg`Remove relation`)}
                            onClick={() => void removeRelation(r.id)}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </Show>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </Show>
            </section>

            {/* ── Locales ───────────────────────────────────────────────── */}
            <section class="space-y-2">
              <div class="flex items-center justify-between">
                <h3 class="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                  {t(msg`Venues`)}
                </h3>
                <Show when={canWrite()}>
                  <Button size="sm" data-testid="venue-create" onClick={() => openVenue()}>
                    <Plus size={14} /> {t(msg`New venue`)}
                  </Button>
                </Show>
              </div>
              <Show
                when={myVenues().length > 0}
                fallback={
                  <p class="text-sm text-muted-foreground" data-testid="venues-empty">
                    {t(msg`No venues yet.`)}
                  </p>
                }
              >
                <For each={myVenues()}>
                  {(v) => (
                    <Card data-testid="venue-item" data-venue-id={v.id}>
                      <CardContent class="space-y-3 py-3">
                        <div class="flex items-start justify-between gap-3">
                          <div class="min-w-0">
                            <p class="font-medium">{v.name}</p>
                            <p class="text-sm text-muted-foreground">
                              {[
                                v.address,
                                (() => {
                                  const { capacity } = v;
                                  return capacity == null ? null : t(msg`Capacity: ${capacity}`);
                                })(),
                                v.opening_hours,
                              ]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                            <Show when={v.purposes}>
                              <div class="mt-1 flex flex-wrap gap-1">
                                <For each={parsePurposes(v.purposes)}>
                                  {(tag) => (
                                    <span
                                      class="rounded-full bg-accent px-2 py-0.5 text-xs text-accent-foreground"
                                      data-testid="venue-purpose"
                                    >
                                      {tag}
                                    </span>
                                  )}
                                </For>
                              </div>
                            </Show>
                          </div>
                          <div class="flex shrink-0 gap-1">
                            <Show when={canWrite()}>
                              <Button
                                size="icon"
                                variant="ghost"
                                aria-label={t(msg`Edit venue`)}
                                data-testid="venue-edit"
                                onClick={() => openVenue(v)}
                              >
                                <Pencil size={14} />
                              </Button>
                              <Button
                                size="icon"
                                variant="ghost"
                                aria-label={t(msg`Add image`)}
                                data-testid="venue-image-add"
                                onClick={() => setImageVenue(v.id)}
                              >
                                <ImagePlus size={14} />
                              </Button>
                            </Show>
                            <Show when={canManage()}>
                              <Button
                                size="icon"
                                variant="ghost"
                                aria-label={t(msg`Move venue to trash`)}
                                onClick={() => void trashVenue(v.id)}
                              >
                                <Trash2 size={14} />
                              </Button>
                            </Show>
                          </div>
                        </div>
                        <Show when={v.lat != null && v.lng != null}>
                          <GeoPreview lat={v.lat ?? 0} lng={v.lng ?? 0} />
                        </Show>
                        <Show when={venueImages(v.id).length > 0}>
                          <div class="flex flex-wrap gap-2" data-testid="venue-gallery">
                            <For each={venueImages(v.id)}>
                              {(img) => (
                                <figure class="group relative">
                                  <img
                                    src={resolveStorageUrl(img.url)}
                                    alt={img.caption ?? v.name ?? ""}
                                    class="h-20 w-28 rounded-md border border-border object-cover"
                                    loading="lazy"
                                    decoding="async"
                                    data-testid="venue-gallery-image"
                                  />
                                  <Show when={canWrite()}>
                                    <button
                                      type="button"
                                      class="absolute right-1 top-1 hidden rounded-full bg-background/80 p-1 group-hover:block"
                                      aria-label={t(msg`Remove image`)}
                                      onClick={() => void removeImage(img.id)}
                                    >
                                      <Trash2 size={12} />
                                    </button>
                                  </Show>
                                </figure>
                              )}
                            </For>
                          </div>
                        </Show>
                      </CardContent>
                    </Card>
                  )}
                </For>
              </Show>
            </section>
          </>
        )}
      </Show>

      {/* ── Diálogos ─────────────────────────────────────────────────────── */}
      <FormDialog
        open={editOpen()}
        onOpenChange={setEditOpen}
        title={t(msg`Edit church`)}
        icon={<ChurchIcon size={20} />}
        submitTestId="church-edit-submit"
        onSubmit={saveEdit}
      >
        <Field
          id="ce-name"
          label={t(msg`Name`)}
          value={field("name")}
          onInput={(v) => setField("name", v)}
        />
        <Field
          id="ce-description"
          label={t(msg`Description`)}
          value={field("description")}
          onInput={(v) => setField("description", v)}
        />
        <Field id="ce-history" label={t(msg`History`)}>
          <TextArea
            id="ce-history"
            rows={3}
            value={field("history")}
            onInput={(v) => setField("history", v)}
            class="font-sans"
          />
        </Field>
        <div class="grid grid-cols-2 gap-3">
          <Field
            id="ce-email"
            label={t(msg`Email`)}
            value={field("email")}
            onInput={(v) => setField("email", v)}
          />
          <Field
            id="ce-phone"
            label={t(msg`Phone`)}
            value={field("phone")}
            onInput={(v) => setField("phone", v)}
          />
          <Field
            id="ce-website"
            label={t(msg`Website`)}
            value={field("website")}
            onInput={(v) => setField("website", v)}
          />
          <Field
            id="ce-whatsapp"
            label={t(msg`WhatsApp link`)}
            value={field("social_whatsapp")}
            onInput={(v) => setField("social_whatsapp", v)}
            placeholder="https://wa.me/…"
            testId="church-social-whatsapp"
          />
          <Field
            id="ce-facebook"
            label={t(msg`Facebook link`)}
            value={field("social_facebook")}
            onInput={(v) => setField("social_facebook", v)}
            placeholder="https://facebook.com/…"
            testId="church-social-facebook"
          />
          <Field
            id="ce-youtube"
            label={t(msg`YouTube link`)}
            value={field("social_youtube")}
            onInput={(v) => setField("social_youtube", v)}
            placeholder="https://youtube.com/…"
            testId="church-social-youtube"
          />
          <Field
            id="ce-instagram"
            label={t(msg`Instagram link`)}
            value={field("social_instagram")}
            onInput={(v) => setField("social_instagram", v)}
            placeholder="https://instagram.com/…"
            testId="church-social-instagram"
          />
          <Field
            id="ce-city"
            label={t(msg`City`)}
            value={field("city")}
            onInput={(v) => setField("city", v)}
          />
          <Field
            id="ce-country"
            label={t(msg`Country`)}
            value={field("country")}
            onInput={(v) => setField("country", v)}
          />
        </div>
        <Field
          id="ce-address"
          label={t(msg`Address`)}
          value={field("address")}
          onInput={(v) => setField("address", v)}
        />
        <Field id="ce-timezone" label={t(msg`Timezone`)}>
          <Select
            options={TIMEZONES.map((tz) => ({ value: tz, label: tz }))}
            value={field("timezone")}
            onChange={(v) => setField("timezone", v)}
            aria-label={t(msg`Timezone`)}
          />
        </Field>
        <div class="flex items-center justify-between rounded-md border border-border px-3 py-2">
          <span class="text-sm">{t(msg`Publicly visible`)}</span>
          <Switch
            checked={isPublic()}
            onCheckedChange={setIsPublic}
            aria-label={t(msg`Publicly visible`)}
          />
        </div>
      </FormDialog>

      <FormDialog
        open={reparentOpen()}
        onOpenChange={setReparentOpen}
        title={t(msg`Move in the tree`)}
        submitTestId="church-reparent-submit"
        onSubmit={reparent}
      >
        <p class="text-sm text-muted-foreground">
          {t(msg`Choose the new parent. The whole subtree moves with this church.`)}
        </p>
        <Field id="reparent-parent" label={t(msg`New parent`)}>
          <Select
            options={reparentOptions()}
            value={newParent()}
            onChange={setNewParent}
            aria-label={t(msg`New parent`)}
          />
        </Field>
      </FormDialog>

      <FormDialog
        open={relationOpen()}
        onOpenChange={setRelationOpen}
        title={t(msg`Add relation`)}
        submitTestId="relation-add-submit"
        disabled={!relationTarget()}
        onSubmit={addRelation}
      >
        <Field id="relation-church" label={t(msg`Related church`)}>
          <Select
            options={relationTargets()}
            value={relationTarget()}
            onChange={setRelationTarget}
            aria-label={t(msg`Related church`)}
          />
        </Field>
        <Field id="relation-kind" label={t(msg`Kind`)}>
          <Select
            options={RELATION_KINDS.map((k) => ({ value: k, label: k }))}
            value={relationKind()}
            onChange={setRelationKind}
            aria-label={t(msg`Kind`)}
          />
        </Field>
      </FormDialog>

      <FormDialog
        open={venueOpen()}
        onOpenChange={setVenueOpen}
        title={venueId() ? t(msg`Edit venue`) : t(msg`New venue`)}
        icon={<MapPin size={20} />}
        submitTestId="venue-submit"
        disabled={!vField("name").trim()}
        onSubmit={saveVenue}
      >
        <Field
          id="v-name"
          label={t(msg`Name`)}
          value={vField("name")}
          onInput={(v) => setVField("name", v)}
          testId="venue-name-input"
        />
        <Field
          id="v-description"
          label={t(msg`Description`)}
          value={vField("description")}
          onInput={(v) => setVField("description", v)}
        />
        <Field
          id="v-address"
          label={t(msg`Address`)}
          value={vField("address")}
          onInput={(v) => setVField("address", v)}
          testId="venue-address-input"
        />
        <div class="grid grid-cols-3 gap-3">
          <Field
            id="v-capacity"
            label={t(msg`Capacity`)}
            type="number"
            value={vField("capacity")}
            onInput={(v) => setVField("capacity", v)}
            testId="venue-capacity-input"
          />
          <Field
            id="v-lat"
            label={t(msg`Latitude`)}
            value={vField("lat")}
            onInput={(v) => setVField("lat", v)}
            testId="venue-lat-input"
          />
          <Field
            id="v-lng"
            label={t(msg`Longitude`)}
            value={vField("lng")}
            onInput={(v) => setVField("lng", v)}
            testId="venue-lng-input"
          />
        </div>
        <Show
          when={
            Number.isFinite(Number(vField("lat"))) &&
            Number.isFinite(Number(vField("lng"))) &&
            vField("lat") &&
            vField("lng")
          }
        >
          <GeoPreview lat={Number(vField("lat"))} lng={Number(vField("lng"))} />
        </Show>
        <p class="text-xs text-muted-foreground">
          {t(
            msg`Coordinates render a built-in preview. Map tiles from an API-key provider are a manual integration.`,
          )}
        </p>
        <Field
          id="v-purposes"
          label={t(msg`Purposes (comma separated)`)}
          value={vField("purposes")}
          onInput={(v) => setVField("purposes", v)}
          placeholder={t(msg`worship, classes, office`)}
          testId="venue-purposes-input"
        />
        <Field
          id="v-hours"
          label={t(msg`Opening hours`)}
          value={vField("opening_hours")}
          onInput={(v) => setVField("opening_hours", v)}
          placeholder={t(msg`Mon-Fri 9:00-18:00`)}
        />
      </FormDialog>

      <FormDialog
        open={imageVenue() !== null}
        onOpenChange={(open) => {
          if (!open) {
            setImageVenue(null);
          }
        }}
        title={t(msg`Add image`)}
        icon={<ImagePlus size={20} />}
        submitTestId="venue-image-submit"
        disabled={!imageUrl().trim()}
        onSubmit={addImage}
      >
        {/* Storage REAL (F3): sube el archivo a la plataforma; la URL
            resultante es la que guarda la fila local-first. */}
        <UploadField
          id="vi-file"
          label={t(msg`Upload image`)}
          testId="venue-image-file"
          onUploaded={setImageUrl}
        />
        <Field
          id="vi-url"
          label={t(msg`…or image URL`)}
          value={imageUrl()}
          onInput={setImageUrl}
          testId="venue-image-url"
        />
        <Field
          id="vi-caption"
          label={t(msg`Caption`)}
          value={imageCaption()}
          onInput={setImageCaption}
        />
        <Show when={imageUrl().trim()}>
          <img
            src={resolveStorageUrl(imageUrl())}
            alt=""
            decoding="async"
            class="max-h-40 w-full rounded-md border border-border object-cover"
            data-testid="venue-image-preview"
          />
        </Show>
      </FormDialog>
    </div>
  );
}
